import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { usePublicProfile } from '../../features/public-profile/use-public-profile';
import { Avatar } from '../../components/ui/avatar';
import { Spinner } from '../../components/ui/spinner';
import { ApiError } from '../../api/types';

export const Route = createFileRoute('/_public/u/$username')({
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const { t } = useTranslation();
  const { username } = Route.useParams();
  const profile = usePublicProfile(username);

  if (profile.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (profile.error instanceof ApiError && profile.error.status === 404) {
    return (
      <div className="mx-auto max-w-2xl px-10 py-16 text-center">
        <h1 className="mb-6 text-5xl font-extralight tracking-tighter text-[var(--color-text-primary)]">
          {t('publicProfile.notFound')}
        </h1>
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-[var(--color-text-muted)]">
          @{username}
        </p>
      </div>
    );
  }

  if (profile.isError || !profile.data) {
    return (
      <div className="mx-auto max-w-2xl px-10 py-16 text-center">
        <p className="text-sm text-[var(--color-danger)]">{t('common.error')}</p>
      </div>
    );
  }

  const data = profile.data;
  return (
    <div className="mx-auto max-w-3xl px-10 py-20">
      <span className="mb-6 block text-xs font-black uppercase tracking-[0.4em] text-[var(--color-text-muted)]">
        {t('app.title')} / Profile
      </span>
      <div className="flex flex-col gap-8 md:flex-row md:items-end">
        <Avatar src={data.avatarUrl} name={data.displayName} size="lg" />
        <div className="flex flex-col gap-3">
          <h1 className="text-7xl font-extralight tracking-tighter leading-[0.9] text-[var(--color-text-primary)]">
            {data.displayName}
          </h1>
          <p className="font-mono text-sm tracking-[0.3em] text-[var(--color-text-muted)]">
            @{data.username}
          </p>
        </div>
      </div>

      <section className="mt-16 border-t border-[var(--color-border-strong)] pt-10">
        <p className="whitespace-pre-line text-lg font-light leading-relaxed text-[var(--color-text-muted)]">
          {data.bio ?? (
            <span className="text-[var(--color-text-muted)]">{t('publicProfile.noBio')}</span>
          )}
        </p>
      </section>
    </div>
  );
}
